import React, { useState, useEffect } from 'react';

interface ReceiptViewerProps {
  url: string;
}

const ReceiptViewer: React.FC<ReceiptViewerProps> = ({ url }) => {
  const [fileType, setFileType] = useState<'image' | 'pdf' | 'unknown'>('unknown');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const determineFileType = async () => {
      try {
        // First, check the URL extension (simple check)
        const urlLower = url.toLowerCase();
        const isLikelyPDF = urlLower.includes('.pdf') || 
                           urlLower.includes('application/pdf') ||
                           url.includes('pdf');
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.bmp'];
        const isLikelyImage = imageExtensions.some(ext => urlLower.includes(ext));

        if (isLikelyPDF) {
          setFileType('pdf');
        } else if (isLikelyImage) {
          setFileType('image');
        } else {
          // Try to fetch headers to determine content type
          try {
            const response = await fetch(url, { method: 'HEAD' });
            const contentType = response.headers.get('content-type');
            
            if (contentType?.includes('application/pdf')) {
              setFileType('pdf');
            } else if (contentType?.includes('image/')) {
              setFileType('image');
            } else {
              setFileType('unknown');
            }
          } catch (fetchError) {
            // If HEAD request fails, default to image first, then fallback
            setFileType('image');
          }
        }
      } catch (err) {
        setFileType('unknown');
        setError('Could not determine file type');
      } finally {
        setLoading(false);
      }
    };

    determineFileType();
  }, [url]);

  const handleImageError = () => {
    // If image fails to load, try as PDF
    if (fileType === 'image') {
      setFileType('pdf');
    } else if (fileType === 'pdf') {
      setError('Failed to load file');
    }
  };

  const handlePDFError = () => {
    // If PDF fails to load, try as image
    if (fileType === 'pdf') {
      setFileType('image');
    } else {
      setError('Failed to load file');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8 bg-gray-50 rounded-lg border border-gray-200 w-full">
        <p className="font-medium text-gray-700">Error loading receipt</p>
        <p className="text-sm text-gray-500 mt-2 mb-4">{error}</p>
        <a 
          href={url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Open in New Tab
        </a>
      </div>
    );
  }

  return (
    <>
      {/* Try PDF first if detected */}
      {fileType === 'pdf' && (
        <div className="w-full">
          <iframe
            src={`${url}#view=FitH`}
            className="w-full h-[600px] rounded-lg border border-gray-200"
            title="Payment Receipt PDF"
            onError={handlePDFError}
          />
          <div className="mt-2 text-center">
            <a 
              href={url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:underline"
            >
              Open in new tab if PDF doesn't load
            </a>
          </div>
        </div>
      )}

      {/* Try image if detected or if PDF failed */}
      {fileType === 'image' && (
        <div className="w-full">
          <img
            src={url}
            alt="Payment Receipt"
            className="w-full h-auto rounded-lg border border-gray-200 max-h-[600px] object-contain"
            onError={handleImageError}
          />
          <div className="mt-2 text-center">
            <a 
              href={url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:underline"
            >
              Open image in new tab
            </a>
          </div>
        </div>
      )}

      {/* Fallback for unknown types */}
      {fileType === 'unknown' && (
        <div className="w-full">
          {/* Try both options sequentially */}
          <div className="hidden">
            <iframe
              src={url}
              className="w-full h-[600px]"
              title="PDF Attempt"
              onLoad={() => setFileType('pdf')}
              onError={() => {
                // Try image next
                const img = new Image();
                img.onload = () => setFileType('image');
                img.onerror = () => setError('File cannot be displayed');
                img.src = url;
              }}
            />
          </div>
          
          <div className="text-center p-8 bg-gray-50 rounded-lg border border-gray-200">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
            </div>
            <p className="font-medium text-gray-700">Loading receipt...</p>
            <p className="text-sm text-gray-500 mt-2">
              If this takes too long, you can{' '}
              <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                open it directly
              </a>
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ReceiptViewer;